import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import EditIcon from '@mui/icons-material/Edit';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import MovieIcon from '@mui/icons-material/Movie';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import React, { useState } from 'react';
import { InformationDto } from 'src/api/information';
import {
  CustomerCardDetails,
  CustomerCardPerson,
  buildCustomerCardPreview,
} from 'src/components/oth/CustomerCardDetails';
import styles from '../Information.module.css';
import { ContactListMember, FileDetails, Team, TeamMember } from '../types';
import {
  hasDeclinedContactShare,
  countHiddenDueToConsent,
  resolveContactListMembers,
} from '../utils/contactListMembers';
import { getFilePresentation } from '../utils/filePresentation';
import { normalizeQuillHtml } from '../utils/richText';
import { TeamCards } from './TeamCards';

type InformationPreviewProps = {
  savedForm: InformationDto;
  collapsedPreviewSections: Record<number, boolean>;
  onTogglePreviewSection: (index: number) => void;
  isLeader: boolean;
  onEdit: () => void;
  fileDetails: Record<string, FileDetails>;
  isFetchingFileDetails: boolean;
  contactListMembers: ContactListMember[];
  openingFileId?: string | null;
  onOpenFile?: (fileId: string) => void;
  teams?: Team[];
  currentUserId?: string;
  circleId: string;
};

const FileTypeIcon: React.FC<{ kind: 'pdf' | 'image' | 'video' | 'file' }> = ({
  kind,
}) => {
  if (kind === 'pdf') {
    return <PictureAsPdfIcon fontSize="small" />;
  }
  if (kind === 'image') {
    return <ImageIcon fontSize="small" />;
  }
  if (kind === 'video') {
    return <MovieIcon fontSize="small" />;
  }
  return <InsertDriveFileIcon fontSize="small" />;
};

const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

const participantLabel = (count: number) =>
  count === 1 ? '1 deltaker' : `${count} deltakere`;

const ContactConsentBanner: React.FC<{
  variant: 'summary' | 'hidden' | 'leader';
  visibleCount: number;
  declinedCount: number;
}> = ({ variant, visibleCount, declinedCount }) => (
  <div className={styles.consentBanner} role="status">
    <span className={styles.consentBannerIcon} aria-hidden="true">
      <LockOutlinedIcon />
    </span>
    <div>
      <strong>
        {variant === 'leader'
          ? `${participantLabel(visibleCount)} kan vises • ${declinedCount} anonymisert for medlemmer`
          : variant === 'summary'
            ? `${participantLabel(visibleCount)} kan vises • ${declinedCount} skjult pga. manglende samtykke`
            : `${participantLabel(declinedCount)} vises ikke`}
      </strong>
      <p>
        {variant === 'leader'
          ? 'Deltakere uten samtykke vises for deg, men er anonymisert for medlemmer.'
          : variant === 'summary'
            ? 'Deltakere som ikke har samtykket er skjult i deltakerappen.'
            : 'De har ikke samtykket til deling av kontaktinformasjon.'}
      </p>
    </div>
  </div>
);

export const InformationPreview: React.FC<InformationPreviewProps> = ({
  savedForm,
  collapsedPreviewSections,
  onTogglePreviewSection,
  isLeader,
  onEdit,
  fileDetails,
  isFetchingFileDetails,
  contactListMembers,
  openingFileId,
  onOpenFile,
  teams = [],
  currentUserId,
  circleId,
}) => {
  const [selectedCard, setSelectedCard] = useState<CustomerCardPerson | null>(
    null
  );

  const openCustomerCard = (
    member: ContactListMember | TeamMember,
    teamName?: string
  ) => {
    setSelectedCard(
      buildCustomerCardPreview(member, contactListMembers, {
        teamName,
        teams,
      })
    );
  };

  if (!savedForm.sections || savedForm.sections.length === 0) {
    return <p className={styles.previewEmpty}>No sections stored yet.</p>;
  }

  return (
    <>
      <div className={styles.previewSectionsList}>
        {savedForm.sections.map((section: any, index: number) => {
          const isCollapsed = collapsedPreviewSections[index] ?? true;
          const normalizedContent = normalizeQuillHtml(section.content ?? '');
          const contentTypes = section.contentTypes ?? ['text', 'files'];
          const contactFields = section.contactFields ?? [
            'name',
            'phone',
            'email',
          ];
          const excludedContactIds = section.excludedContactIds ?? [];
          const allContactMembers = resolveContactListMembers(
            contactListMembers,
            section.contactMembers ?? []
          );
          const declinedCount = countHiddenDueToConsent(
            contactListMembers,
            section.contactMembers ?? [],
            excludedContactIds
          );
          const visibleContactMembers = allContactMembers.filter(
            (member) =>
              !excludedContactIds.includes(member.id) &&
              !hasDeclinedContactShare(member)
          );
          const listedContactMembers = allContactMembers.filter((member) => {
            if (hasDeclinedContactShare(member)) {
              return isLeader;
            }
            return !excludedContactIds.includes(member.id);
          });

          return (
            <div key={index} className={styles.previewSectionContainer}>
              <div className={styles.previewSectionHeader}>
                <button
                  type="button"
                  className={styles.previewSectionHeaderButton}
                  onClick={() => onTogglePreviewSection(index)}
                  aria-expanded={!isCollapsed}
                >
                  <span className={styles.previewSectionTitle}>
                    {section.title || `Section ${index + 1}`}
                  </span>
                  <span className={styles.previewChevron}>
                    {isCollapsed ? <CaretDownOutlined /> : <CaretUpOutlined />}
                  </span>
                </button>
                {isLeader && (
                  <button
                    type="button"
                    className={styles.iconButton}
                    onClick={onEdit}
                    aria-label="Edit information"
                  >
                    <EditIcon
                      sx={{ color: 'var(--brand-accent-text, #f0544f)' }}
                    />
                  </button>
                )}
              </div>

              {!isCollapsed && (
                <div className={styles.previewSectionBody}>
                  {contentTypes.includes('text') && normalizedContent && (
                    <div
                      className={styles.previewSectionContent}
                      dangerouslySetInnerHTML={{
                        __html: normalizedContent,
                      }}
                    />
                  )}

                  {contentTypes.includes('files') &&
                    section.fileIds &&
                    section.fileIds.length > 0 && (
                      <div className={styles.previewFiles}>
                        <ul className={styles.previewFilesList}>
                          {section.fileIds.map((fileId: string) => {
                            const file = fileDetails[fileId];
                            const label = file?.name ?? 'Attachment';
                            const contentType = file?.contentType || '';
                            const { kind, fileTypeLabel } = getFilePresentation(
                              label,
                              contentType
                            );
                            const showFetchSkeleton =
                              isFetchingFileDetails && !file;
                            const isOpening = openingFileId === fileId;
                            const canOpen =
                              Boolean(onOpenFile) && !showFetchSkeleton;

                            return (
                              <li
                                key={fileId}
                                className={`${styles.previewFileItem} ${
                                  showFetchSkeleton
                                    ? styles.previewFileSkeletonItem
                                    : ''
                                }`}
                              >
                                <span
                                  className={styles.previewFileTypeIcon}
                                  aria-hidden="true"
                                >
                                  {showFetchSkeleton ? (
                                    <span
                                      className={`${styles.skeletonBlock} ${styles.skeletonSquare}`}
                                    />
                                  ) : (
                                    <FileTypeIcon kind={kind} />
                                  )}
                                </span>
                                <div className={styles.previewFileMeta}>
                                  {showFetchSkeleton ? (
                                    <>
                                      <span
                                        className={`${styles.skeletonBlock} ${styles.skeletonLine} ${styles.skeletonLinePrimary}`}
                                      />
                                      <span
                                        className={`${styles.skeletonBlock} ${styles.skeletonLine} ${styles.skeletonLineSecondary}`}
                                      />
                                    </>
                                  ) : (
                                    <>
                                      {canOpen ? (
                                        <button
                                          type="button"
                                          className={styles.previewFileLink}
                                          onClick={() => onOpenFile?.(fileId)}
                                          disabled={isOpening}
                                        >
                                          {label}
                                        </button>
                                      ) : (
                                        <span
                                          className={styles.previewFileLink}
                                        >
                                          {label}
                                        </span>
                                      )}
                                      <span
                                        className={styles.previewFileTypeLabel}
                                      >
                                        {fileTypeLabel}
                                      </span>
                                    </>
                                  )}
                                </div>
                                {canOpen && (
                                  <button
                                    type="button"
                                    className={styles.previewDownloadFileButton}
                                    onClick={() => onOpenFile?.(fileId)}
                                    disabled={isOpening}
                                    aria-label={`Open ${label}`}
                                  >
                                    <span>
                                      {isOpening ? 'Opening…' : 'Open'}
                                    </span>
                                  </button>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                  {contentTypes.includes('contactList') && (
                    <div className={styles.previewContactList}>
                      {declinedCount > 0 && (
                        <ContactConsentBanner
                          variant={isLeader ? 'leader' : 'summary'}
                          visibleCount={visibleContactMembers.length}
                          declinedCount={declinedCount}
                        />
                      )}
                      {listedContactMembers.length === 0 &&
                        declinedCount === 0 && (
                          <p className={styles.contactListEmpty}>
                            No circle members to display.
                          </p>
                        )}
                      {listedContactMembers.length > 0 && (
                        <div className={styles.previewContactTiles}>
                          {listedContactMembers.map((member) => {
                            const isAnonymized =
                              isLeader && hasDeclinedContactShare(member);
                            const hasContactDetails =
                              (contactFields.includes('phone') &&
                                Boolean(member.phone)) ||
                              (contactFields.includes('email') &&
                                Boolean(member.email));
                            const showIdentity = contactFields.includes('name');

                            return (
                              <article
                                key={member.id}
                                className={
                                  showIdentity && hasContactDetails
                                    ? `${styles.previewContactTile} ${styles.previewContactTileSplit} ${styles.previewContactTileClickable}`
                                    : `${styles.previewContactTile} ${styles.previewContactTileClickable}`
                                }
                                onClick={() => openCustomerCard(member)}
                                onKeyDown={(event) => {
                                  if (
                                    event.key === 'Enter' ||
                                    event.key === ' '
                                  ) {
                                    event.preventDefault();
                                    openCustomerCard(member);
                                  }
                                }}
                                role="button"
                                tabIndex={0}
                                aria-label={
                                  isAnonymized
                                    ? `Vis kundekort for ${member.name}, anonymisert for medlemmer`
                                    : `Vis kundekort for ${member.name}`
                                }
                              >
                                {showIdentity && (
                                  <div
                                    className={
                                      styles.previewContactIdentityButton
                                    }
                                  >
                                    {member.avatarUrl ? (
                                      <img
                                        src={member.avatarUrl}
                                        alt=""
                                        className={styles.previewContactAvatar}
                                      />
                                    ) : (
                                      <span
                                        className={
                                          styles.previewContactInitials
                                        }
                                      >
                                        {getInitials(member.name)}
                                      </span>
                                    )}
                                    <div
                                      className={styles.previewContactHeading}
                                    >
                                      <strong>{member.name}</strong>
                                      {contactFields.includes('company') &&
                                        member.company && (
                                          <span>{member.company}</span>
                                        )}
                                      {isAnonymized && (
                                        <span
                                          className={styles.anonymizedBadge}
                                        >
                                          <VisibilityOffOutlinedIcon />
                                          Anonymisert for medlemmer
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                                {hasContactDetails && (
                                  <div className={styles.previewContactActions}>
                                    {contactFields.includes('phone') &&
                                      member.phone && (
                                        <a
                                          href={`tel:${member.phone}`}
                                          className={styles.previewContactPhone}
                                          onClick={(event) =>
                                            event.stopPropagation()
                                          }
                                        >
                                          <PhoneOutlinedIcon />
                                          {member.phone}
                                        </a>
                                      )}
                                    {contactFields.includes('email') &&
                                      member.email && (
                                        <a
                                          href={`mailto:${member.email}`}
                                          className={styles.previewContactEmail}
                                          title={member.email}
                                          onClick={(event) =>
                                            event.stopPropagation()
                                          }
                                        >
                                          <EmailOutlinedIcon />
                                          <span
                                            className={
                                              styles.previewContactEmailText
                                            }
                                          >
                                            {member.email}
                                          </span>
                                        </a>
                                      )}
                                  </div>
                                )}
                                {contactFields.includes('notes') &&
                                  member.notes && (
                                    <div className={styles.previewContactNotes}>
                                      <NotesOutlinedIcon />
                                      {member.notes}
                                    </div>
                                  )}
                              </article>
                            );
                          })}
                        </div>
                      )}
                      {!isLeader && declinedCount > 0 && (
                        <ContactConsentBanner
                          variant="hidden"
                          visibleCount={visibleContactMembers.length}
                          declinedCount={declinedCount}
                        />
                      )}
                    </div>
                  )}

                  {contentTypes.includes('teams') && teams.length > 0 && (
                    <TeamCards
                      teams={teams}
                      currentUserId={currentUserId}
                      isLeader={isLeader}
                      onSelectMember={(member, team) =>
                        openCustomerCard(member, team.name)
                      }
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <CustomerCardDetails
        open={Boolean(selectedCard)}
        tribeId={circleId}
        preview={selectedCard}
        viewerRole={isLeader ? 'leader' : 'member'}
        currentUserId={currentUserId}
        onClose={() => setSelectedCard(null)}
      />
    </>
  );
};
